import { useRef } from "react";
import { createFileRoute, notFound } from "@tanstack/react-router";

import { CaseAbout } from "../components/portfolio/CaseAbout";
import { CaseColumns } from "../components/portfolio/CaseColumns";
import { CaseFacts } from "../components/portfolio/CaseFacts";
import { CaseFeatures } from "../components/portfolio/CaseFeatures";
import { CaseGallery } from "../components/portfolio/CaseGallery";
import { CaseHero } from "../components/portfolio/CaseHero";
import { CaseIdentity } from "../components/portfolio/CaseIdentity";
import { CaseNav } from "../components/portfolio/CaseNav";
import { CaseRelated } from "../components/portfolio/CaseRelated";
import { CaseScale } from "../components/portfolio/CaseScale";
import { CaseServices } from "../components/portfolio/CaseServices";
import { CaseSteps } from "../components/portfolio/CaseSteps";
import { CaseStickyBar } from "../components/portfolio/CaseStickyBar";
import { CaseTechCards } from "../components/portfolio/CaseTechCards";


import { CaseResponsive } from "../components/portfolio/CaseResponsive";
import { CaseStory } from "../components/portfolio/CaseStory";
import { SystemLogisticCase } from "../components/portfolio/editorial/SystemLogisticCase";

import { LeadForm } from "../components/shared/LeadForm";
import { AutoScale } from "../components/site/AutoScale";
import { Breadcrumbs } from "../components/site/Breadcrumbs";
import { Footer } from "../components/site/Footer";
import { Header } from "../components/site/Header";
import { NotFoundView } from "../components/site/NotFoundView";
import { ScrollToTop } from "../components/site/ScrollToTop";
import { SpecialOffer } from "../components/home/SpecialOffer";
import { getCase, getPrevNext } from "../content/portfolio";
import { SITE_NAME, SITE_URL, buildMeta } from "../lib/site";

export const Route = createFileRoute("/cases/$slug")({
  loader: ({ params }) => {
    const study = getCase(params.slug);
    if (!study) throw notFound();
    return { slug: study.slug };
  },
  head: ({ params }) => {
    const study = getCase(params.slug);
    if (!study) {
      return buildMeta({
        title: `Кейс не найден — ${SITE_NAME}`,
        description: "Такой проект не найден. Посмотрите другие кейсы hay.agency.",
        path: `/cases/${params.slug}`,
        noindex: true,
      });
    }

    const { seo } = study;
    const base = buildMeta({
      title: seo.title,
      description: seo.description,
      path: `/cases/${seo.slug}`,
      image: seo.ogImage,
      imageWidth: 1200,
      imageHeight: 630,
      imageAlt: seo.h1,
      locale: "ru_RU",
      noindex: !seo.robotsIndex,
    });

    return {
      ...base,
      meta: [
        ...base.meta.filter(
          (m) => m["property"] !== "og:title" && m["property"] !== "og:description",
        ),
        { property: "og:title", content: seo.ogTitle },
        { property: "og:description", content: seo.ogDescription },
      ],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@graph": [
              {
                "@type": "BreadcrumbList",
                itemListElement: [
                  ["Главная", "/"],
                  ["Наши кейсы", "/cases"],
                  [study.industry.label, "/cases"],
                  [study.cardTitle, `/cases/${study.slug}`],
                ].map(([name, path], i) => ({
                  "@type": "ListItem",
                  position: i + 1,
                  name,
                  item: `${SITE_URL}${path}`,
                })),
              },
              {
                "@type": "CreativeWork",
                "@id": `${seo.canonicalUrl}#case`,
                name: seo.h1,
                headline: seo.h1,
                description: seo.description,
                url: seo.canonicalUrl,
                inLanguage: "ru-RU",
                datePublished: seo.datePublished,
                dateModified: seo.dateModified,
                image: seo.ogImage,
                keywords: [seo.primaryKeyword, ...seo.secondaryKeywords].join(", "),
                about: seo.semanticEntities,
                genre: "Case study",
                creator: { "@id": `${SITE_URL}/#organization` },
                publisher: { "@id": `${SITE_URL}/#organization` },
                isPartOf: { "@id": `${SITE_URL}/#website` },
                mainEntityOfPage: { "@type": "WebPage", "@id": seo.canonicalUrl },
                audience: {
                  "@type": "BusinessAudience",
                  audienceType: study.client.industry,
                },
                mentions: {
                  "@type": "Organization",
                  name: study.client.name,
                  ...(study.client.legalName ? { legalName: study.client.legalName } : {}),
                  url: study.client.url,
                  areaServed: study.client.city,
                },
              },
            ],
          }),
        },
      ],
    };
  },
  component: CasePage,
  notFoundComponent: CaseNotFound,
});

function CaseNotFound() {
  return (
    <NotFoundView
      eyebrow="Кейс не найден"
      slogans={[
        ["Такого", "проекта"],
        ["у нас", "нет"],
      ]}
      description="Возможно, ссылка устарела. Посмотрите другие проекты агентства или напишите нам — расскажем о похожем опыте."
      primary={{ label: "Смотреть кейсы", to: "/cases" }}
      secondary={{ label: "Связаться", to: "/contacts" }}
    />
  );
}

function CasePage() {
  const { slug } = Route.useLoaderData();
  const study = getCase(slug)!;
  const { prev, next } = getPrevNext(slug);
  const isEditorial = slug === "system-logistic";
  const heroRef = useRef<HTMLSpanElement>(null);

  return (
    <AutoScale>
      <div className="min-h-screen bg-background font-sans text-foreground antialiased">
        <SpecialOffer />
        <Header />
        <CaseStickyBar study={study} heroRef={heroRef} />

        <main>
          <div className="mx-auto w-full px-5 pt-2 lg:px-[21px] lg:pt-[28px]">
            <Breadcrumbs
              current={study.cardTitle}
              trail={[
                { label: "Наши кейсы", to: "/cases" },
                { label: study.industry.label, to: "/cases" },
              ]}
            />
          </div>

          {isEditorial ? (
            <SystemLogisticCase ref={heroRef} study={study} />
          ) : (
            <>
              <CaseHero ref={heroRef} study={study} />

              <CaseFacts study={study} />
              {study.about ? <CaseAbout about={study.about} /> : null}
              {study.challenge ? <CaseSteps challenge={study.challenge} /> : null}
              {study.columnBlocks?.map((block) => (
                <CaseColumns key={block.id} block={block} />
              ))}
              {study.story.length
                ? study.story.map((section) => (
                    <CaseStory key={section.title} sections={[section]} />
                  ))
                : null}
              {study.features.length ? <CaseFeatures features={study.features} /> : null}
              {study.gallery.length ? (
                <CaseGallery title="Макеты сайта" images={study.gallery} />
              ) : null}
              {study.identity.fonts.length || study.identity.colors.length ? (
                <CaseIdentity study={study} />
              ) : null}
              <CaseStory sections={[study.design]} />
              <CaseResponsive study={study} />
              {study.tech ? <CaseTechCards tech={study.tech} /> : null}
              {study.scale ? <CaseScale scale={study.scale} /> : null}
              {study.servicesDone ? <CaseServices services={study.servicesDone} /> : null}
            </>
          )}

          <CaseRelated links={study.links} />
          <CaseNav prev={prev} next={next} />

          <div id="case-form">
            <LeadForm
              title={
                <>
                  Давайте обсудим, как мы можем масштабировать
                  <br className="hidden lg:block" /> ваш бизнес
                </>
              }
              lead="Оставьте заявку — свяжемся в течение двух часов, чтобы задать вопросы и обсудить задачу."
              submitLabel={study.cta.button}
            />
          </div>
        </main>
        <Footer />
        <ScrollToTop />
      </div>
    </AutoScale>
  );
}
